import { type ArticleFormInput, type Article, type FeedbackForm, type FeedbackSubmission, type Sharepoint, Part } from './types'
import { type APIArticle, APIArticleFromJSON } from '../../../generated-types/models/APIArticle'
import { type APICategory } from '../../../generated-types/models/APICategory'
import { type APIFeedbackForm, APIFeedbackFormFromJSON } from '../../../generated-types/models/APIFeedbackForm'
import { type APIFeedbackSubmission, APIFeedbackSubmissionFromJSON } from '../../../generated-types/models/APIFeedbackSubmission'
import { type APIProperty } from '../../../generated-types/models/APIProperty'
import { type APISharepoint, APISharepointFromJSON } from '../../../generated-types/models/APISharepoint'
import LogTool from '../../../logger/logTools'
import {
  createContact,
  createGuestUser,
  createRole,
  createUser,
  createUserPreferences,
  getPKfromSelf,
  parseNestedObject,
  parseNestedObjectList,
} from '../../../utils/functions'
import { type GuestUser } from '../../../utils/types'
import { createRequest } from '../../Request/utils/functions'
import { type Request } from '../../Request/utils/types'
import { createSupplier } from '../../Supplier/utils/functions'
import { type Category, type Property, type Supplier } from '../../Supplier/utils/types'
import { createGuestRequest } from '../../UnauthRequest/utils/functions'
import { type GuestRequest } from '../../UnauthRequest/utils/types'
import { APIPart, APIPartFromJSON } from '../../../generated-types'

const log = new LogTool({ context: 'itemUtils', enable: true, logLevel: 'warn' })

/* -------------------------------------------------------------------------- */
/*                           Utility functions start                          */
/* -------------------------------------------------------------------------- */

/**
 * Maps the Category and Process to the corresponding Process for the ProcessForm
 * @param category of Manufacturing Process
 * @param process for selected Category
 * @returns process name that is used for conditionally rendering the ProcessForm
 */
export const mapCategoryAndProcessToProcess = (category: string, process: string) => {
  const categoryAndProcess: string = category + '_' + process
  const processMapping: any = {
    ASSEMBLY_manual: 'assembly_manually',
    ASSEMBLY_robot: 'assembly_robot',
    JOINING_welding: 'joining_welding',
    JOINING_soldering: 'joining_soldering',
    JOINING_glueing: 'joining_glueing',
    CNCMACHINING_milling: 'cnc_milling',
    CNCMACHINING_drilling: 'cnc_drilling',
    CNCMACHINING_turning: 'cnc_turning',
    CNCMACHINING_grinding: 'cnc_grinding',
    CASTING_dieCast: 'casting_die_cast',
    'CASTING_Injection Moulding': 'casting_injection_moulding',
    'CASTING_Sand Casting': 'casting_sand_casting',
    'CASTING_Investment Casting': 'casting_investment_casting',
    PCB_etching: 'pcb_etching',
    POSTPROCESSING_blasting: 'postprocessing_blasting',
    POSTPROCESSING_brush: 'postprocessing_brush',
    POSTPROCESSING_grinding: 'postprocessing_grinding',
    POSTPROCESSING_polishing: 'postprocessing_polishing',
    POSTPROCESSING_painting: 'postprocessing_painting',
    POSTPROCESSING_anodizing: 'postprocessing_anodizing',
    POSTPROCESSING_electroPlatedNickelPlating: 'postprocessing_electroPlatedNickelPlating',
    POSTPROCESSING_electroPlatedChromePlating: 'postprocessing_electroPlatedChromePlating',
    POSTPROCESSING_laserEngraving: 'postprocessing_laserEngraving',
    POSTPROCESSING_powderCoating: 'postprocessing_powderCoating',
    POSTPROCESSING_electroGalvanizing: 'postprocessing_electroGalvanizing',
    POSTPROCESSING_customProcess: 'postprocessing_customProcess',
    SHEETMETAL_tubeLaserCutting: 'sheetmetal_tubeLaserCutting',
    SHEETMETAL_punching: 'sheetmetal_punching',
    SHEETMETAL_bending: 'sheetmetal_bending',
    SHEETMETAL_folding: 'sheetmetal_folding',
    SHEETMETAL_laserCutting: 'sheetmetal_laserCutting',
    PRINTING3D_3DPrinting: 'printing3D_3DPrinting',
    PRINTING3D_FDM: 'printing3D_FDM',
    PRINTING3D_SLA: 'printing3D_SLA',
    PRINTING3D_SLS: 'printing3D_SLS',
    PRINTING3D_SLM: 'printing3D_SLM',
    PRINTING3D_DLP: 'printing3D_DLP',
    PRINTING3D_PBF: 'printing3D_PBF',
    PRINTING3D_DMLS: 'printing3D_DMLS',
    PRINTING3D_MJF: 'printing3D_MJF',
    STANDARDCOMPONENT_standardComponent: 'standardComponent_standardComponent',
    RAWMATERIAL_rawMaterial: 'rawMaterial_rawMaterial',
    QUALITYCONTROL_qualityControl: 'qualityControl_qualityControl',
    CUSTOMPROCESS_customProcess: 'customProcess_customProcess',
  }
  return processMapping[categoryAndProcess]
}

/**
 * Convert an Article-Server-Response into an Article object.
 * @param responseObject The APIArticle response Object.
 * @returns
 */
export function createArticle(responseObject: any): Article {
  const normalizedResponse =
    typeof responseObject === 'object' && responseObject !== null
      ? (responseObject as Record<string, unknown>)
      : {}
  const hasSnakeCaseFields =
    'created_at' in normalizedResponse ||
    'last_modified_at' in normalizedResponse ||
    'has_children' in normalizedResponse ||
    'quality_class' in normalizedResponse ||
    'article_type' in normalizedResponse ||
    'hoops_part_id' in normalizedResponse
  const hasCamelCaseFields =
    'createdAt' in normalizedResponse ||
    'lastModifiedAt' in normalizedResponse ||
    'hasChildren' in normalizedResponse ||
    'qualityClass' in normalizedResponse ||
    'articleType' in normalizedResponse ||
    'hoopsPartId' in normalizedResponse
  const a: APIArticle =
    hasCamelCaseFields && !hasSnakeCaseFields
      ? (normalizedResponse as unknown as APIArticle)
      : APIArticleFromJSON(normalizedResponse)
  return {
    ...a,
    // ...((a.leadTime !== undefined && a.leadTime !== null) && {leadTime: parseDuration(a.leadTime)}),
    ...(a.articleType !== undefined && a.articleType !== null && { type: a.articleType }),
    key: getPKfromSelf(a.self),
    supplier: a.supplier ? createSupplier(a.supplier) : null,
    /*guestRequest: parseNestedObject(a.guestRequest, createGuestRequest),*/
    hoopsPartId: a.hoopsPartId ? a.hoopsPartId : ((normalizedResponse as any)?.hoopsPartId ?? undefined),
    properties: Array.isArray(a.properties)
      ? a.properties.filter((property: string | null): property is string => property !== null)
      : undefined,
    category: Array.isArray(a.category)
      ? a.category.filter((category: string | null): category is string => category !== null)
      : undefined,
    request: parseNestedObject((normalizedResponse as any).request, createRequest),
    requests: parseNestedObjectList(a.requests, createRequest),
    leadTime: a.leadTime ? { months: a.leadTime.months.toString(), weeks: a.leadTime.weeks.toString(), days: a.leadTime.days.toString() } : undefined,
  }
}

/**
 * Convert an Part-Server-Response into an Part object.
 * @param responseObject The APIPart response Object.
 * @returns
 */
export function createPart(responseObject: any): Part {
  const normalizedResponse =
    typeof responseObject === 'object' && responseObject !== null
      ? (responseObject as Record<string, unknown>)
      : {}
  const hasSnakeCaseFields =
    'created_at' in normalizedResponse ||
    'last_modified_at' in normalizedResponse ||
    'has_children' in normalizedResponse ||
    'quality_class' in normalizedResponse ||
    'article_type' in normalizedResponse ||
    'hoops_part_id' in normalizedResponse
  const hasCamelCaseFields =
    'createdAt' in normalizedResponse ||
    'lastModifiedAt' in normalizedResponse ||
    'hasChildren' in normalizedResponse ||
    'qualityClass' in normalizedResponse ||
    'articleType' in normalizedResponse ||
    'hoopsPartId' in normalizedResponse
  const p: APIPart =
    hasCamelCaseFields && !hasSnakeCaseFields
      ? (normalizedResponse as unknown as APIPart)
      : APIPartFromJSON(normalizedResponse)
  return {
    ...p,
    // ...((a.leadTime !== undefined && a.leadTime !== null) && {leadTime: parseDuration(a.leadTime)}),
    key: getPKfromSelf(p.self),
    /*guestRequest: parseNestedObject(a.guestRequest, createGuestRequest),*/
    hoopsPartId: p.hoopsPartId ? p.hoopsPartId : ((normalizedResponse as any)?.hoopsPartId ?? undefined),
    properties: Array.isArray(p.properties)
      ? p.properties.filter((property: string | null): property is string => property !== null)
      : undefined,
    request: parseNestedObject((normalizedResponse as any).request, createRequest),
    requests: parseNestedObjectList(p.requests, createRequest),
  }
}

export function getArticleRepresentation(article: Article, maxLength = -1): string {
  let representation = ''
  if (article.name) {
    representation += `${article.name} (${article.number})`
  } else {
    representation += `${article.number}`
  }

  if (maxLength > 0 && representation.length > maxLength) {
    // shorten the representation to the specified length
    representation = representation.slice(0, maxLength) + ' ...'
  }

  return representation
}

export function createCategory(responseObject: any): Category {
  const c: APICategory = responseObject
  return {
    ...c,
    key: getPKfromSelf(c.self),
  }
}

export function createProperty(responseObject: any): Property {
  const p: APIProperty = responseObject
  return {
    ...p,
    key: getPKfromSelf(p.self),
  }
}

export function createFeedbackForm(responseObject: any): FeedbackForm {
  log.debug('feedbackFormInput ->', responseObject)

  // The generated API clients already convert snake_case to camelCase.
  // Avoid running APIFeedbackFormFromJSON on an object that is already in camelCase.
  const isAlreadyParsed =
    responseObject &&
    typeof responseObject === 'object' &&
    'formJson' in responseObject &&
    !('form_json' in responseObject)

  const f: APIFeedbackForm = isAlreadyParsed
    ? (responseObject as APIFeedbackForm)
    : APIFeedbackFormFromJSON(responseObject)

  const formFields = Array.isArray((f as any)?.formJson) ? (f as any).formJson : []

  return {
    ...f,
    formJson: formFields.map((formField: any) => {
      log.debug('rename properties of formField', formField)
      // we need to convert the 'form_field_type' property back into a 'type' property.
      // In the backend 'type' is a reserved keyword and therefore properties with that
      // name are renamed into non reserved names (e.g. 'form_field_type').
      const normalizedField = {
        ...formField,
        type: formField.formFieldType ?? formField.type,
        // make sure that the formFields are rendered with the specified size at every screen-size
        sm: formField.xl,
        md: formField.xl,
        lg: formField.xl,
      }
      delete normalizedField.formFieldType
      return normalizedField
    }),
    key: getPKfromSelf(f.self),
  }
}

export function createFeedbackSubmission(responseObject: any): FeedbackSubmission {
  const s: APIFeedbackSubmission = APIFeedbackSubmissionFromJSON(responseObject)
  log.debug('APIFeedbackSubmission ->', s)
  return {
    ...s,
    key: getPKfromSelf(s.self),
    // sharepoint: parseNestedObject(s.sharepoint, createSharepoint),
    article: parseNestedObject(s.article, createArticle),
    // the submitted by is automatically parsed by the APIFeedbackSubmissionFromJSON function
    // -> we only need to convert it from APIGuestUser to GuestUser
    submittedBy: {
      ...s.submittedBy,
      key: getPKfromSelf(s.submittedBy.self),
      roles: parseNestedObjectList(s.submittedBy.roles, createRole),
      repFor: parseNestedObject(s.submittedBy.repFor, createContact),
      preferences: parseNestedObject(s.submittedBy.preferences, createUserPreferences),
    } as GuestUser,
  }
}

export function createSharepoint(responseObject: any): Sharepoint {
  const s: APISharepoint = APISharepointFromJSON(responseObject)
  return {
    ...s,
    key: getPKfromSelf(s.self),
    createdBy: parseNestedObject(s.createdBy, createUser),
    sharedArticles: parseNestedObjectList(s.sharedArticles, createArticle) as unknown as Article[],
    sharedBOMs: parseNestedObjectList(s.sharedBOMs, createArticle) as unknown as Article[],
    sharedGuestRequest: parseNestedObject(
      s.sharedGuestRequest,
      createGuestRequest
    ) as unknown as GuestRequest,
    sharedRequest: parseNestedObject(s.sharedRequest, createRequest) as unknown as Request,
    sharedWith: parseNestedObjectList(s.sharedWith, createGuestUser) as unknown as GuestUser[],
    feedbackForm: parseNestedObject(s.feedbackForm, createFeedbackForm),
    feedbackSubmissions: parseNestedObjectList(
      s.feedbackSubmissions,
      createFeedbackSubmission
    ) as unknown as FeedbackSubmission[],
    unreadFeedbackSubmissionCount: Number(s.unreadFeedbackSubmissionCount),
  }
}

/**
 * Convert an Article object to the JSON representation of the corresponding APIArticle object.
 * @param a Article to convert.
 * @returns
 */
export function formInputToAPICategoryJSON(input: any) {
  return {
    ...(input.name && { name: input.name }),
  }
}

export function formInputToAPIPropertyJSON(input: any) {
  return {
    ...(input.name && { name: input.name }),
    ...(input.value && { value: input.value }),
    ...(input.type && { type: input.type }),
  }
}

export function createOption(
  responseObject: any,
  url: 'v1/supplier/suppliers/' | 'v1/items/categories/' | 'v1/items/properties/'
): Supplier | Category | Property {
  switch (url) {
    case 'v1/supplier/suppliers/':
      return createSupplier(responseObject)
    case 'v1/items/categories/':
      return createCategory(responseObject)
    case 'v1/items/properties/':
      return createProperty(responseObject)
    default:
      throw new Error(
        `Error in createOption! Provided url ${url} does not match any of the valid urls v1/supplier/suppliers/, v1/items/categories/, or v1/items/properties/.`
      )
  }
}

/**
 * Convert an Article-Object into a data-representation that can be used by the Form component.
 * @param article The article to convert.
 * @returns
 */
export function getFormObjectFromArticle(article: Article): ArticleFormInput {
  // typecast to enable access to expanded properties
  const a: any = article as NonNullable<unknown>
  const fallbackLabelFromUrl = (url: string) => {
    try {
      const parts = url.split('/').filter(Boolean)
      return decodeURIComponent(parts[parts.length - 1] || url)
    } catch (_e) {
      return url
    }
  }
  const result = {
    ...a,
    barcodeType: a.barcodeType,
    type: typeof a.type === 'object' && a.type !== null ? a.type.name : a.type,
    family: typeof a.family === 'object' && a.family !== null ? a.family.name : a.family,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    properties: a.properties?.map((property: any) => {
      // Ensure value is always a URL string
      // If property is a string, use it directly (should be a URL)
      // If property is an object, use property.self (which should be a URL)
      const propertyValue = typeof property === 'string'
        ? property
        : (typeof property === 'object' && property?.self ? property.self : undefined)
      const propertyLabel = typeof property === 'object' && property?.name
        ? property.name
        : (typeof property === 'string' ? fallbackLabelFromUrl(property) : '')
      // Only include if we have a valid URL value
      if (!propertyValue) {
        return null
      }
      return {
        value: propertyValue,
        label: propertyLabel,
        disabled: false,
      }
    })?.filter(Boolean),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    category: a.category?.map((category: any) => {
      // Ensure value is always a URL string
      // If category is a string, use it directly (should be a URL)
      // If category is an object, use category.self (which should be a URL)
      const categoryValue = typeof category === 'string'
        ? category
        : (typeof category === 'object' && category?.self ? category.self : undefined)
      const categoryLabel = typeof category === 'object' && category?.name
        ? category.name
        : (typeof category === 'string' ? fallbackLabelFromUrl(category) : '')
      // Only include if we have a valid URL value
      if (!categoryValue) {
        return null
      }
      return {
        value: categoryValue,
        label: categoryLabel,
        disabled: false,
      }
    })?.filter(Boolean),
    variationOf:
      typeof a.variationOf === 'object' && a.variationOf !== null
        ? { value: a.variationOf.self, label: getArticleRepresentation(a.variationOf) }
        : '',
    supplier: a.supplier ? { value: a.supplier?.self, label: a.supplier?.name } : '',
    // Store measurements as objects with value and unit to support unit selection
    weight: a.weight ? { value: a.weight.value ?? '', unit: a.weight.unit ?? 'g' } : '',
    length: a.length ? { value: a.length.value ?? '', unit: a.length.unit ?? 'm' } : '',
    width: a.width ? { value: a.width.value ?? '', unit: a.width.unit ?? 'm' } : '',
    height: a.height ? { value: a.height.value ?? '', unit: a.height.unit ?? 'm' } : '',
    volume: a.volume ? { value: a.volume.value ?? '', unit: a.volume.unit ?? 'cubic_meter' } : '',
  }
  return result
}

export function cubic_symbol(unit: string): string {
  switch (unit) {
    case 'cubic_meter':
      return 'm³'
    case 'cubic_decimeter':
      return 'dm³'
    case 'cubic_centimeter':
      return 'cm³'
    case 'cubic_millimeter':
      return 'mm³'
    default:
      return 'm³'
  }
}

/**
 * Unit conversion definitions for automatic unit scaling
 */
type UnitConversion = {
  baseUnit: string
  conversions: Array<{ unit: string; factor: number; displaySymbol: string }>
}

const unitConversions: Record<string, UnitConversion> = {
  // Length units (base: meter)
  m: {
    baseUnit: 'm',
    conversions: [
      { unit: 'mm', factor: 0.001, displaySymbol: 'mm' },
      { unit: 'cm', factor: 0.01, displaySymbol: 'cm' },
      { unit: 'm', factor: 1, displaySymbol: 'm' },
      { unit: 'km', factor: 1000, displaySymbol: 'km' },
    ],
  },
  // Weight units (base: gram)
  g: {
    baseUnit: 'g',
    conversions: [
      { unit: 'mg', factor: 0.001, displaySymbol: 'mg' },
      { unit: 'g', factor: 1, displaySymbol: 'g' },
      { unit: 'kg', factor: 1000, displaySymbol: 'kg' },
    ],
  },
  // Volume units (base: cubic_meter)
  cubic_meter: {
    baseUnit: 'cubic_meter',
    conversions: [
      { unit: 'cubic_millimeter', factor: 1e-9, displaySymbol: 'mm³' },
      { unit: 'cubic_centimeter', factor: 1e-6, displaySymbol: 'cm³' },
      { unit: 'cubic_meter', factor: 1, displaySymbol: 'm³' },
    ],
  },
}

/**
 * Finds the best unit for a given value by converting to the most readable unit.
 * Prefers units where the value is between 0.1 and 1000 (excluding boundaries).
 */
function findBestUnit(
  value: number,
  unitConversions: UnitConversion
): { value: number; unit: string; displaySymbol: string } {
  const absValue = Math.abs(value)

  // Find the best unit - prefer values between 0.1 and 1000
  for (let i = unitConversions.conversions.length - 1; i >= 0; i--) {
    const conversion = unitConversions.conversions[i]
    const convertedValue = absValue / conversion.factor

    // If the converted value is between 0.1 and 1000, use this unit
    if (convertedValue >= 0.1 && convertedValue < 1000) {
      return {
        value: value / conversion.factor,
        unit: conversion.unit,
        displaySymbol: conversion.displaySymbol,
      }
    }
  }

  // If no unit fits perfectly, use the unit that gives the smallest value >= 0.1
  // or the largest unit if all values are < 0.1
  for (let i = unitConversions.conversions.length - 1; i >= 0; i--) {
    const conversion = unitConversions.conversions[i]
    const convertedValue = absValue / conversion.factor

    if (convertedValue >= 0.1) {
      return {
        value: value / conversion.factor,
        unit: conversion.unit,
        displaySymbol: conversion.displaySymbol,
      }
    }
  }

  // Fallback: use the smallest unit
  const smallestUnit = unitConversions.conversions[0]
  return {
    value: value / smallestUnit.factor,
    unit: smallestUnit.unit,
    displaySymbol: smallestUnit.displaySymbol,
  }
}

/**
 * Formats a measurement value with its unit for human-readable display.
 * Automatically converts to appropriate units (e.g., 1000m → 1km, 1000g → 1kg)
 * and rounds the value to an appropriate number of decimal places.
 * 
 * @param value - The numeric value to format (can be number, string, null or undefined)
 * @param unit - The unit string (can be null or undefined)
 * @returns Formatted string with value and unit, or '-' if value/unit is missing
 * 
 * @example
 * formatMeasurement(0.9400000000000001, 'm') // Returns '0.94m'
 * formatMeasurement(1000, 'm') // Returns '1km'
 * formatMeasurement(0.001, 'm') // Returns '1mm'
 * formatMeasurement(1000, 'g') // Returns '1kg'
 * formatMeasurement(0.000892, 'cubic_meter') // Returns '0.89dm³'
 * formatMeasurement(null, 'm') // Returns '-'
 */
export function formatMeasurement(value: number | string | null | undefined, unit: string | null | undefined): string {
  // Handle missing values
  if (value === null || value === undefined || unit === null || unit === undefined) {
    return '-'
  }

  // Convert string to number if needed
  let numericValue: number
  if (typeof value === 'string') {
    numericValue = parseFloat(value)
    // If conversion fails, return '-'
    if (isNaN(numericValue)) {
      return '-'
    }
  } else {
    numericValue = value
  }

  // Handle NaN or Infinity
  if (!Number.isFinite(numericValue)) {
    return '-'
  }

  // Check if we have unit conversion rules for this unit
  let convertedValue = numericValue
  let displayUnit: string

  // Handle volume units
  if (unit.startsWith('cubic_')) {
    const volumeConversion = unitConversions.cubic_meter
    const bestUnit = findBestUnit(numericValue, volumeConversion)
    convertedValue = bestUnit.value
    displayUnit = bestUnit.displaySymbol
  } else if (unitConversions[unit]) {
    // Handle length and weight units
    const conversion = unitConversions[unit]
    const bestUnit = findBestUnit(numericValue, conversion)
    convertedValue = bestUnit.value
    displayUnit = bestUnit.displaySymbol
  } else {
    // Unknown unit - use as-is with cubic_symbol for volume-like units
    displayUnit = unit.startsWith('cubic_') ? cubic_symbol(unit) : unit
  }

  // Determine decimal places based on magnitude of converted value
  let decimalPlaces: number
  const absConvertedValue = Math.abs(convertedValue)

  if (absConvertedValue < 1) {
    // Small values: use 2-3 decimal places
    // For very small values (< 0.01), use 3 decimal places
    decimalPlaces = absConvertedValue < 0.01 ? 3 : 2
  } else if (absConvertedValue < 100) {
    // Medium values: use 1-2 decimal places
    decimalPlaces = absConvertedValue < 10 ? 2 : 1
  } else {
    // Large values: use 0-1 decimal places
    decimalPlaces = absConvertedValue < 1000 ? 1 : 0
  }

  // Round the value to the determined decimal places
  // Use toFixed and parseFloat to handle floating-point precision issues
  const roundedValue = parseFloat(convertedValue.toFixed(decimalPlaces))

  // Format the number (removes trailing zeros)
  const formattedValue = roundedValue.toString()

  return `${formattedValue}${displayUnit}`
}

/* -------------------------------------------------------------------------- */
/*                             API functions start                            */
/* -------------------------------------------------------------------------- */
/* 
 * NOTE: All API functions have been migrated to articleApi.ts and React Query hooks.
 * The following functions have been removed:
 * - fetchArticles -> use useArticles() from hooks/useArticleQueries
 * - fetchArticle -> use useArticle() from hooks/useArticleQueries
 * - postArticle -> use useCreateArticle() from hooks/useArticleMutations
 * - updateArticle -> use useUpdateArticle() from hooks/useArticleMutations
 * - fetchArticleBrands -> use useBrands() from hooks/useArticleQueries
 * - fetchArticleCategories -> use useAllCategories() from hooks/useArticleQueries
 * - fetchArticleProperties -> use useAllProperties() from hooks/useArticleQueries
 * - fetchFeedbackForms -> use useFeedbackForms() from hooks/useArticleQueries
 * - fetchSharepoints -> use useSharepoints() from hooks/useArticleQueries
 * - handleAddOption -> use useCreateCategoryOrProperty() from hooks/useArticleMutations
 */
